import { GAME_STATES } from "./StateController.js";
import { buildNarrativeWrapper } from "./NarrativeEngine.js";
import { formatLabel } from "./Utils.js";
import { logger } from "./Logger.js";
import { getBroadDomain } from "./coreRepoJSONL.js";

/**
 * GuardController manages the active medical shift (guardia).
 * It coordinates the runner, UI, and state changes.
 */
export class GuardController {
  constructor(runner, ui, stateCtrl, vitals, feedback, uiManager, audio, adaptive, passiveSystem, economy, callbacks = {}) {
    this.runner = runner;
    this.ui = ui;
    this.stateCtrl = stateCtrl;
    this.vitals = vitals;
    this.feedback = feedback;
    this.uiManager = uiManager;
    this.audio = audio;
    this.adaptive = adaptive;
    this.passiveSystem = passiveSystem;
    this.economy = economy;
    this.callbacks = callbacks; // { refreshUI, terminateGuard }
    
    this.encounterRetryCount = 0;
    this.resetLearningState();
  }

  resetLearningState() {
    this.learningState = { 
      xp: 0, 
      streak: 0, 
      bestStreak: 0, 
      correct: 0, 
      incorrect: 0, 
      total: 0,
      totalStability: 0,
      insights: []
    };
  }

  async showNextEncounter() {
    logger.info("[GuardController] Iniciando showNextEncounter...");

    // Clear previous UI to prevent ghosting and indicate loading
    this.ui.enunciado.innerHTML = '<div class="loading-clinical" style="text-align:center; padding: 40px; opacity: 0.8;">' +
      '<div class="spinner" style="margin-bottom:15px; font-size: 2rem;">⌛</div>' +
      '<div style="font-size: 0.95em;">Cargando siguiente caso clínico...</div></div>';
    this.ui.options.innerHTML = '';

    // Safety Watchdog: If nothing renders in 8s, something is seriously wrong
    const watchdog = setTimeout(() => {
        const loadingEl = this.ui.enunciado.querySelector('.loading-clinical');
        if (loadingEl) {
            logger.error("[GuardController] WATCHDOG TRIGGERED: Rendering timeout.");
            logger.error("[GuardController] Current session state:", this.runner.currentSession);
            
            if (this.encounterRetryCount >= 2) {
              logger.error("[GuardController] Max watchdog retries reached. Exiting cleanly.");
              this.encounterRetryCount = 0;
              if (this.uiManager.notify) {
                this.uiManager.notify("❌ Error persistente al cargar el caso. Volviendo al menú...", "error");
              }
              this.callbacks.terminateGuard(false);
              return;
            }

            this.encounterRetryCount++;
            if (this.uiManager.notify) {
              this.uiManager.notify("⚠️ No se pudo cargar el caso. Reintentando...", "warning");
            }
            // Retry once before giving up
            setTimeout(() => this.showNextEncounter(), 1500);
        }
    }, 8000);

    const node = this.runner.getNextEncounter();
    if (!node) {
      clearTimeout(watchdog);
      const session = this.runner.currentSession;
      if (session && session.currentIndex >= session.encounters.length) {
        logger.info("Fin de la guardia alcanzado.");
        return this.callbacks.terminateGuard(true, this.learningState);
      } else {
        logger.error("[GuardController] No se pudo obtener encuentro. Session:", session);
        this.uiManager.notify("❌ Error al cargar el caso. Reiniciando...", "error");
        return this.callbacks.terminateGuard(false);
      }
    }
    
    const session = this.runner.currentSession;
    if (!session) {
      clearTimeout(watchdog);
      logger.error("No se encontró una sesión activa al mostrar encuentro.");
      return this.callbacks.terminateGuard(false);
    }

    this.hintsUsedThisEncounter = 0;

    try {
      this.uiManager.updateProgress(session.currentIndex, session.encounters.length, session.isChain);

      const currentTheme = ['theme-morning', 'theme-afternoon', 'theme-night']
        .find(t => document.body.classList.contains(t)) || 'theme-morning';
      const activeMentorId = this.economy.state.activeMentor || 'default';
      const caseIndex = session.currentIndex || 0;

      logger.debug(`Rindiendo encuentro: ${node.id} de tema: ${node.topic || node.tema}`);

      const isBlind = this.economy.rpg?.classId === 'urgenciologo';
      
      if (this.ui.caseMeta) {
        this.ui.caseMeta.textContent = isBlind ? "⚠️ VISIÓN DE TÚNEL" : formatLabel(node.topic || node.tema || 'General');
      }

      // Narrative injection
      this.ui.enunciado.innerHTML = ''; // Limpiar previo
      this.ui.enunciado.innerHTML = buildNarrativeWrapper(node, {
        shiftTheme: currentTheme,
        activeMentorId,
        caseIndex,
        isBlind: isBlind,
        isChain: this.runner.currentSession.isChain
      });

      // Juice: Trigger Entrance Animation [cite: 1, 9]
      this.ui.enunciado.classList.remove('encounter-enter');
      void this.ui.enunciado.offsetWidth; // Force reflow
      this.ui.enunciado.classList.add('encounter-enter');

      clearTimeout(watchdog);
      this.encounterRetryCount = 0;

      this.ui.options.innerHTML = '';
      let visibleIdx = 1;
      
      this.currentShuffledOptions = [...node.opciones];
      for (let i = this.currentShuffledOptions.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [this.currentShuffledOptions[i], this.currentShuffledOptions[j]] = [this.currentShuffledOptions[j], this.currentShuffledOptions[i]];
      }
      
      this.currentShuffledOptions.forEach((opt, idx) => {
        if (opt._hidden) return;
        const btn = document.createElement('button');
        btn.className = 'option-btn card';
        btn.innerHTML = `<span class="opt-idx">${visibleIdx}</span> ${opt.texto}`;
        btn.onclick = () => this.handleChoice(opt, node);
        this.ui.options.appendChild(btn);
        visibleIdx++;
      });

    if (node.letal_si_falla) this.ui.gameArea.classList.add('lethal-mode');
    else this.ui.gameArea.classList.remove('lethal-mode');

    // [RPG] Setup Active Skills (mentor + class)
    this._setupActiveSkill(activeMentorId);
    this._setupClassSkill();

    // Modo estudio ("Mis Apuntes"): razonamiento reflexivo, sin presión de tiempo.
    // Se omite el timer/monitor clínico para no inducir adivinación mientras se construye el conocimiento.
    const studyMode = !!this.runner.currentSession?.isLibrary;
    if (studyMode) {
      this.vitals.clear();
      if (this.ui.vitalsTimer) {
        this.ui.vitalsTimer.textContent = '∞';
        this.ui.vitalsTimer.title = 'Modo estudio: sin límite de tiempo';
      }
    } else {
      this.vitals.start(this.passiveSystem, this.economy, this.runner.currentSession, this.runner.modifiers, node);
    }

    if (this.runner.currentSession) {
      const currentStability = this.runner.currentSession.patientState.estabilidad;
      this.uiManager.updateVitals(this.runner.currentSession.patientState.vitals, currentStability);
      this.learningState.totalStability += currentStability;
      this.learningState.total++;
    }
    
    this._setupHint(node);
    } catch (err) {
      logger.error("[GuardController] Fallo en showNextEncounter:", err);
      this.callbacks.terminateGuard(false);
    }
  }

  handleTimeout() {
    logger.warn("[GuardController] Tiempo agotado. Procesando como respuesta incorrecta.");
    if (!this.runner.currentSession) return;
    const encounters = this.runner.currentSession.encounters;
    const idx = this.runner.currentSession.currentIndex;
    const node = encounters[idx];
    if (!node) {
      this.callbacks.terminateGuard(false);
      return;
    }
    const dummyOpt = { es_correcta: false, texto: "[TIEMPO AGOTADO]" };
    this.handleChoice(dummyOpt, node);
  }

  _setupActiveSkill(mentorId) {
    const btn = document.getElementById('btn-active-skill');
    if (!btn) return;

    const mentorsDB = window.MENTORS_DB || [];
    const mentor = mentorsDB.find(m => m.id === mentorId);
    
    const nameEl = document.getElementById('skill-name');
    const descEl = document.getElementById('skill-desc');

    if (!mentor || !mentor.active_skill_name || !mentor.skill_id) {
        btn.parentElement.style.display = 'none';
        return;
    }

    btn.parentElement.style.display = 'block';
    if (nameEl) nameEl.textContent = mentor.active_skill_name;
    if (descEl) descEl.textContent = mentor.active_skill_desc;

    // The runner keys skills by their clinical skill_id, not the mentor id
    const skillId = mentor.skill_id;

    // Check if used this session
    const isUsed = this.runner.currentSession?.skillsUsed?.[skillId];
    btn.disabled = !!isUsed;
    btn.style.opacity = isUsed ? "0.5" : "1";

    btn.onclick = async () => {
        if (this.runner.currentSession?.skillsUsed?.[skillId]) return;

        logger.info(`Activando habilidad: ${mentor.active_skill_name}`);
        this.audio.playSuccess();

        const result = await this.runner.useActiveSkill(skillId);
        if (result) {
            this.uiManager.notify(`¡${mentor.active_skill_name} activado!`, 'success');
            btn.disabled = true;
            btn.style.opacity = "0.5";
            this._applySkillResult(result);
        }
    };
  }

  // Sets up the active skill granted by the player's chosen class (Internista/Urgenciólogo/Cirujano)
  _setupClassSkill() {
    const btn = document.getElementById('btn-class-skill');
    if (!btn) return;

    const classDef = this.economy.rpg?.getClassDef?.();
    const active = classDef?.active;
    const nameEl = document.getElementById('class-skill-name');
    const descEl = document.getElementById('class-skill-desc');

    if (!classDef || !active) {
        btn.parentElement.style.display = 'none';
        return;
    }

    btn.parentElement.style.display = 'block';
    if (nameEl) nameEl.textContent = active.name;
    if (descEl) descEl.textContent = active.desc;

    const skillId = active.id;
    const isUsed = this.runner.currentSession?.skillsUsed?.[skillId];
    btn.disabled = !!isUsed;
    btn.style.opacity = isUsed ? "0.5" : "1";

    btn.onclick = async () => {
        if (this.runner.currentSession?.skillsUsed?.[skillId]) return;

        logger.info(`Activando habilidad de clase: ${active.name}`);
        this.audio.playSuccess();

        const result = await this.runner.useActiveSkill(skillId);
        if (result) {
            this.uiManager.notify(`¡${active.name} activado!`, 'success');
            btn.disabled = true;
            btn.style.opacity = "0.5";
            this._applySkillResult(result);
        }
    };
  }

  // Shared handling of an active-skill result (mentor or class)
  _applySkillResult(result) {
    if (!result) return;

    // Pedagogical Insights [cite: 5, 10]
    if (result.effect === 'reveal_reasoning') {
        this.uiManager.showMentorInsight(result.reasoning);
    } else if (result.effect === 'reveal_elimination') {
        this.uiManager.showMentorInsight(result.logic);
        this._refreshOptions(this.runner.getNextEncounter());
    } else if (result.effect === 'reduce_burnout') {
        this.economy.state.burnout = Math.max(0, this.economy.state.burnout + result.burnoutDelta);
        this.uiManager.applyVisualBurnout(this.economy.state.burnout);
    } else if (result.effect === 'add_time') {
        if (this.vitals && typeof this.vitals.addTime === 'function') {
            this.vitals.addTime(15);
        }
    } else if (result.effect === 'ignore_lethal') {
        this.uiManager.notify("¡Protección de Soporte Vital Activa!", "success");
    }

    // Normal refresh if flags exist
    if (result.refreshOptions && result.effect !== 'reveal_elimination') {
        this._refreshOptions(this.runner.getNextEncounter());
    }

    this.uiManager.updateVitals(this.runner.currentSession.patientState.vitals, this.runner.currentSession.patientState.estabilidad);
  }

  _refreshOptions(node) {
      if (!node) return;
      this.ui.options.innerHTML = '';
      let visibleIdx = 1;
      
      const optionsToUse = this.currentShuffledOptions || node.opciones;
      
      optionsToUse.forEach((opt, idx) => {
          if (opt._hidden) return; // Support for 50/50 hidden options
          const btn = document.createElement('button');
          btn.className = 'option-btn card';
          btn.innerHTML = `<span class="opt-idx">${visibleIdx}</span> ${opt.texto}`;
          btn.onclick = () => this.handleChoice(opt, node);
          this.ui.options.appendChild(btn);
          visibleIdx++;
      });
  }

  async handleChoice(opt, node) {
    this.vitals.clear();
    const coreResPromise = this.runner.processDecision(opt.id || opt.texto)
        .catch(err => { 
            logger.error("[GuardController] Fallo en processDecision:", err);
            return { result: 'error', reason: 'Exception in processing' }; 
        });
    
    const coreRes = await coreResPromise;
    const isCorrect = coreRes.result === 'correct';
    
    // Calculate if it was answered slow
    const limitMs = this.vitals.state.limitMs || 45000;
    const remaining = this.vitals.state.endTime ? (this.vitals.state.endTime - Date.now()) : 0;
    const isSlow = remaining < (limitMs * 0.15) && remaining > 0; // Considered slow if less than 15% time left
    
    this.adaptive.registerOutcome(node, isCorrect, isSlow);
    
    // Conectar FSRS-Lite (Phase G4)
    const domain = getBroadDomain(node);
    this.economy.updateTopicStats(domain, isCorrect);
    
    if (isCorrect) {
      logger.info(`Respuesta correcta en: ${node.id}`);
      this.learningState.correct++;
      this.learningState.streak++;
      this.learningState.bestStreak = Math.max(this.learningState.bestStreak, this.learningState.streak);
      let diffVal = node.dificultad || node.difficulty || 1;
      if (typeof diffVal === 'string') {
        const lower = diffVal.toLowerCase();
        if (lower === 'baja' || lower === 'easy') diffVal = 1;
        else if (lower === 'media' || lower === 'standard' || lower === 'medium') diffVal = 2;
        else if (lower === 'alta' || lower === 'hard') diffVal = 3;
        else diffVal = 1;
      }
      
      let caseXp = (diffVal || 1) * 10;
      
      // Apply Perk XP Multipliers (Fase 2)
      const modifiers = this.runner.modifiers;
      if (modifiers && modifiers.scoring) {
        const nodeTags = (node.tags || []).map(t => t.toLowerCase());
        if (node.topic) nodeTags.push(node.topic.toLowerCase());
        if (node.tema) nodeTags.push(node.tema.toLowerCase());
        
        let tagMultiplier = 1;
        nodeTags.forEach(tag => {
          if (modifiers.scoring.xpMultipliers[tag]) {
            tagMultiplier *= modifiers.scoring.xpMultipliers[tag];
          }
        });
        caseXp *= tagMultiplier;

        if (modifiers.scoring.xpDifficultyMultipliers[diffVal]) {
          caseXp *= modifiers.scoring.xpDifficultyMultipliers[diffVal];
        }
      }
      
      if (this.runner.currentSession?.doubleCreditsNextCorrect) {
        this.runner.currentSession.patientState.doubleCreditsBonus = (this.runner.currentSession.patientState.doubleCreditsBonus || 0) + 50;
        this.runner.currentSession.doubleCreditsNextCorrect = false; // Consumed
        this.uiManager.notify("📐 ¡Proporción Áurea: Bono de créditos activado!", "success");
      }

      this.learningState.xp += Math.round(caseXp);
      this.audio.playSuccess();
      this.uiManager.showResultFeedback('correct');
      if (navigator.vibrate) navigator.vibrate(50); // Haptic reinforcement for success
    } else {
      logger.warn(`Respuesta incorrecta en: ${node.id}. Burnout aumentado.`);
      this.learningState.incorrect++;
      this.learningState.streak = 0;
      const riskDelta = this.runner.currentSession.patientState.risk;
      
      const modifiers = this.runner.modifiers;
      if (modifiers?.guard?.firstErrorNoBurnout && this.learningState.incorrect === 1) {
        this.uiManager.notify("🛡️ ¡Segunda Opinión: Burnout mitigado en tu primer error!", "info");
      } else {
        let burnoutGain = riskDelta * 0.2;
        if (this.runner.currentSession?.feynmanActive) {
          burnoutGain *= 0.5; // Feynman active skill: 50% fatigue reduction
        }
        if (this.passiveSystem) {
          burnoutGain = this.passiveSystem.applyFatigueReduction(burnoutGain);
        }
        this.economy.addBurnout(burnoutGain);
      }

      this.audio.playFailure();
      this.uiManager.showResultFeedback('error');
      if (navigator.vibrate) navigator.vibrate([50, 50, 50]); // Warning vibration for error
      
      // Juice: Trigger Patient Monitor Error Glitch [cite: 1, 9]
      if (this.ui.patientMonitor) {
          this.ui.patientMonitor.classList.remove('ekg-error');
          void this.ui.patientMonitor.offsetWidth; // Force reflow
          this.ui.patientMonitor.classList.add('ekg-error');
          setTimeout(() => this.ui.patientMonitor.classList.remove('ekg-error'), 1000);
      }
    }

    // Record case outcome for precision streaks (Fase 2)
    const badgeAwarded = this.economy.recordCaseOutcome(isCorrect);
    if (badgeAwarded) {
      this.uiManager.notify("🏆 ¡Insignia de Excelencia obtenida por racha de 50 aciertos!", "success");
    }

    this.uiManager.updateVitals(this.runner.currentSession.patientState.vitals, this.runner.currentSession.patientState.estabilidad);
    this.uiManager.applyVisualBurnout(this.economy.state.burnout);

    // [Quests] Advance daily missions based on this answer
    this.callbacks.onQuestEvent?.({
      result: isCorrect ? 'correct' : 'incorrect',
      domain: domain,
      streak: this.learningState.streak,
      isLibrary: !!this.runner.currentSession?.isLibrary
    });

    // [Atlas] Record the Pearl [cite: 1, 9]
    try {
        this.economy.recordPearl(node.id, node);
    } catch (err) {
        logger.error("[GuardController] Fallo al registrar perla clínica:", err);
    }


        // Show result in the persistent overlay
        if (this.ui.feedbackOverlay) {
            if (this.stateCtrl) this.stateCtrl.changeState(GAME_STATES.FEEDBACK);
            this.ui.feedbackOverlay.style.display = 'flex';
            this.ui.feedbackBody.innerHTML = this.feedback.buildLearningFlashcard(node, {
              retro: coreRes.retro || (isCorrect ? node.perla || '¡Correcto!' : opt.retroalimentacion || 'Error clínico.'),
              correct_option_text: coreRes.correct_option_text || node.opciones.find(o => o.resultado === 'correcto')?.texto || 'N/A'
            }, isCorrect, isSlow);

            this.ui.nextBtn.onclick = () => {
              this.audio.playClick();
              this.ui.feedbackOverlay.style.display = 'none';
              if (this.stateCtrl) this.stateCtrl.changeState(GAME_STATES.ENCOUNTER);
              this.showNextEncounter();
            };

            // Handle lethal error buttons (check coreRes.isLethal, not node flag, so Reanimación is respected)
            if (coreRes.isLethal && !isCorrect) {
                this.ui.nextBtn.style.display = 'none';
                this.ui.exitLethal.style.display = 'block';
                this.ui.exitLethal.onclick = () => {
                    this.ui.feedbackOverlay.style.display = 'none';
                    this.ui.exitLethal.style.display = 'none';
                    this.ui.nextBtn.style.display = 'block';
                    this.callbacks.terminateGuard(false, this.learningState);
                };
            }
        }
  }

  _setupHint(node) {
    if (!this.ui.hintArea) return;
    this.ui.hintArea.style.display = 'block';
    
    const hintBtn = document.getElementById('hint-button');
    const hintText = document.getElementById('hint-text');

    const isInternista = this.economy.rpg?.classId === 'internista';

    if (isInternista) {
        if (hintText) {
            hintText.textContent = this.feedback.buildHint(node);
            hintText.style.display = 'block';
        }
        if (hintBtn) hintBtn.disabled = true;
    } else {
        if (hintText) hintText.style.display = 'none';
        if (hintBtn) {
            hintBtn.disabled = false;
            hintBtn.onclick = () => {
                if (hintText) {
                    hintText.textContent = this.feedback.buildHint(node);
                    hintText.style.display = 'block';
                }
                hintBtn.disabled = true;
                this.audio.playClick();

                // Burnout cost (+5) modified by passive system and perks
                const modifiers = this.runner.modifiers;
                const isFree = modifiers?.guard?.freeFirstHint && this.hintsUsedThisEncounter === 0;
                if (!isFree) {
                    let burnoutGain = 5;
                    if (this.passiveSystem) {
                        burnoutGain = this.passiveSystem.applyFatigueReduction(burnoutGain);
                    }
                    this.economy.addBurnout(burnoutGain);
                    this.uiManager.applyVisualBurnout(this.economy.state.burnout);
                    if (this.ui.burnoutUI) {
                        this.ui.burnoutUI.textContent = `${Math.round(this.economy.state.burnout)}%`;
                    }
                } else {
                    this.uiManager.notify("💡 ¡Diagnóstico Diferencial: Primera pista gratis!", "info");
                }
                this.hintsUsedThisEncounter++;
            };
        }
    }
  }

}
